import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to check if the current user has a specific permission
 * @param permission - Permission string to check (e.g., "WorkOrders.Create")
 * @returns true if user has the permission, false otherwise
 */
export const usePermission = (permission: string): boolean => {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
};

/**
 * Hook to check if the current user has ANY of the specified permissions
 * @param permissions - Array of permission strings
 * @returns true if user has at least one of the permissions
 */
export const useHasAnyPermission = (permissions: string[]): boolean => {
  const { hasAnyPermission } = useAuth();
  return hasAnyPermission(permissions);
};

/**
 * Hook to check if the current user has ALL of the specified permissions
 * @param permissions - Array of permission strings
 * @returns true if user has all of the permissions
 */
export const useHasAllPermissions = (permissions: string[]): boolean => {
  const { hasAllPermissions } = useAuth();
  return hasAllPermissions(permissions);
};

/**
 * Hook to get the current user's role
 * @returns the user's role string or null
 */
export const useUserRole = (): string | null => {
  const { role } = useAuth();
  return role;
};

/**
 * Hook to get all current user's permissions
 * @returns array of permission strings
 */
export const useUserPermissions = (): string[] => {
  const { permissions } = useAuth();
  return permissions;
};
